import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Linkedin, Twitter, Facebook } from 'lucide-react';
import './ContactUs.css';

// TODO: swap back to the business address once testing is done
const CONTACT_EMAIL = 'info@ecodesignprotect.rw';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // honeypot (should stay empty)
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Trim only leading spaces for name/subject to prevent accidental blanks
    const next = (name === 'name' || name === 'subject') ? value.replace(/^\s+/, '') : value;
    setFormData({ ...formData, [name]: next });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) return; // bot filled the honeypot

    if (!e.currentTarget.checkValidity()) {
      e.currentTarget.reportValidity();
      return;
    }

    setIsSubmitting(true);
    setIsSubmitted(false);
    setSubmitError('');

    try {
      const response = await fetch('/send-contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(formData)
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || 'We could not send your message. Please try again.');
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '', website: '' });
      window.setTimeout(() => setIsSubmitted(false), 7000);
    } catch (error) {
      setSubmitError(error.message || 'We could not send your message. Please email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-us-page">
      {/* Breadcrumb (optional; matches Services style) */}
      <nav className="contact-breadcrumbs" aria-label="Breadcrumb">
        <ol>
          <li><a href="/">Home</a></li>
          <li aria-current="page">Contact Us</li>
        </ol>
      </nav>

      {/* HERO */}
      <section className="contact-us-hero" role="region" aria-labelledby="contact-hero-heading">
        <div className="contact-us-hero-content">
          <h1 id="contact-hero-heading">Let’s build something good!</h1>
          <p>Tell us about your goals. We’ll reply within 1 business day.</p>
        </div>
      </section>

      {/* GLASS PANEL */}
      <div className="contact-us-content" role="region" aria-labelledby="contact-info-heading">
        {/* Info */}
        <section className="contact-us-info" aria-labelledby="contact-info-heading">
          <h2 id="contact-info-heading">Contact Information</h2>
          <p>Fill up the form and our team will get back to you.</p>

          <div className="contact-us-info-items">
            <div className="contact-us-info-item">
              <span className="contact-us-icon"><Phone size={20} aria-hidden="true" /></span>
              <p><a className="contact-us-link" href="tel:+250788306105">+250 788 306 105</a></p>
            </div>
            <div className="contact-us-info-item">
              <span className="contact-us-icon"><Mail size={20} aria-hidden="true" /></span>
              <p><a className="contact-us-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
            </div>
            <div className="contact-us-info-item">
              <span className="contact-us-icon"><MapPin size={20} aria-hidden="true" /></span>
              <p>Kimihurura, Ubumwe House, 2nd Floor, KG 5 Avenue</p>
            </div>
          </div>

          <div className="contact-us-social-links" aria-label="Social links">
            <a href="#" className="contact-us-social-link" aria-label="LinkedIn"><Linkedin size={18} /></a>
            <a href="#" className="contact-us-social-link" aria-label="Twitter / X"><Twitter size={18} /></a>
            <a href="#" className="contact-us-social-link" aria-label="Facebook"><Facebook size={18} /></a>
          </div>
        </section>

        {/* Form */}
        <section className="contact-us-form" aria-labelledby="send-message-heading">
          <h2 id="send-message-heading">Send Us A Message</h2>

          <form onSubmit={handleSubmit} noValidate>
            {/* Honeypot field (hidden from users) */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="contact-us-form-row">
              <div className="contact-us-form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  inputMode="text"
                  autoComplete="name"
                  placeholder="Jane Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="contact-us-form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="contact-us-form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                placeholder="How can we help?"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>

            <div className="contact-us-form-group">
              <label htmlFor="message">Your Message</label>
              <textarea
                id="message"
                name="message"
                placeholder="Write your message…"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
              />
            </div>

            <button type="submit" className="contact-us-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send Message'}
              <Send size={18} aria-hidden="true" />
            </button>

            {/* Submission status, announced to screen readers */}
            <div
              className={`contact-us-status-message success ${isSubmitted ? 'show' : ''}`}
              role="status"
              aria-live="polite"
            >
              Thank you. Your message has been sent successfully.
            </div>
            <div
              className={`contact-us-status-message error ${submitError ? 'show' : ''}`}
              role="alert"
            >
              {submitError} {submitError && <a href={`mailto:${CONTACT_EMAIL}`}>Email us directly</a>}
            </div>
          </form>
        </section>
      </div>

      {/* Map */}
      <section className="contact-us-map" aria-labelledby="map-heading">
        <h2 id="map-heading" className="visually-hidden">Map — Ubumwe House</h2>
        <div className="contact-map-frame">
          <iframe
            title="Ubumwe House location map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3987.5089488543644!2d30.058958715396318!3d-1.9534099985747084!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x19dca5d5f7890ebf%3A0x1d57cde1dc888b89!2sUbumwe%20House!5e0!3m2!1sen!2suk!4v1629801023830!5m2!1sen!2suk"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}

export default ContactUs;
